import React, { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../utils/serverConfig';

const detectCategory = (item = {}) => {
  const name = (item.name || item.id || '').toString().toLowerCase();
  if (!name) return 'Other';
  if (name.includes('raffle') || name.includes('ticket')) return 'Raffle';
  if (name.includes('tee') || name.includes('shirt') || name.includes('rash') || name.includes('merch')) return 'Merch';
  if (name.includes('private') || name.includes('lesson') || name.includes('session')) return 'Private Lesson';
  if (name.includes('membership') || name.includes('plan')) return 'Membership';
  return 'Other';
};

const short = (s = '') => (s && s.toString().length > 12 ? `${s.toString().slice(0,8)}...${s.toString().slice(-4)}` : s);

export default function StudentPurchaseHistory({ wallet, student = null, orderHistory = [], onViewCollectable = () => {} }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [payments, setPayments] = useState([]);

  const fetchByWallet = useCallback(async (w) => {
    try {
      let walletToUse = w;
      if (!walletToUse) {
        try {
          if (window.ethereum && window.ethereum.request) {
            const accs = await window.ethereum.request({ method: 'eth_accounts' });
            if (accs && accs.length) walletToUse = accs[0];
          }
        } catch (e) {
          // ignore wallet probe failure
        }
      }
      if (!walletToUse) return setOrders([]);
      setLoading(true);
      setError(null);
      const encoded = encodeURIComponent(walletToUse);
      const res = await apiFetch(`/api/orders/by-wallet/${encoded}`);
      if (!res.ok) throw new Error(`Server ${res.status}`);
      const data = await res.json();
      const arr = Array.isArray(data.orders) ? data.orders : data;
      setOrders(arr || []);
    } catch (e) {
      console.warn('fetchByWallet failed', e);
      setError(String(e && (e.message || e)));
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch by order IDs (orderHistory) when available, otherwise fallback to wallet probe
  const fetchByOrderHistory = useCallback(async (ids = []) => {
    try {
      if (!ids || ids.length === 0) return setOrders([]);
      setLoading(true);
      setError(null);
      // fetch all orders and filter locally (server does not expose single-order GET)
      const res = await apiFetch('/api/orders');
      if (!res.ok) throw new Error(`Server ${res.status}`);
      const data = await res.json();
      const arr = Array.isArray(data.orders) ? data.orders : (Array.isArray(data) ? data : []);
      const matched = arr.filter(o => ids.includes(o.id) || (o.meta && o.meta && ids.includes(o.meta.orderId)));
      setOrders(matched);
    } catch (e) {
      console.warn('fetchByOrderHistory failed', e);
      setError(String(e && (e.message || e)));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPaymentsForStudent = useCallback(async (studentObj) => {
    try {
      if (!studentObj || !studentObj.id) return setPayments([]);
      const res = await apiFetch(`/api/payments?studentId=${encodeURIComponent(studentObj.id)}`);
      if (!res.ok) return setPayments([]);
      const json = await res.json().catch(() => null) || [];
      const arr = Array.isArray(json) ? json : (json.payments || json.records || []);
      setPayments(arr || []);
    } catch (e) {
      console.warn('fetchPaymentsForStudent failed', e);
      setPayments([]);
    }
  }, []);

  useEffect(() => {
    // Priority: if orderHistory is provided use it, else use wallet probe
    if (Array.isArray(orderHistory) && orderHistory.length > 0) {
      fetchByOrderHistory(orderHistory);
    } else {
      fetchByWallet(wallet);
    }
    // also fetch payments for the student if present
    if (student) fetchPaymentsForStudent(student);
    const onNew = (ev) => {
      try {
        const detail = ev && ev.detail ? ev.detail : null;
        // If no detail provided, refresh based on current inputs
        if (!detail) {
          if (Array.isArray(orderHistory) && orderHistory.length > 0) fetchByOrderHistory(orderHistory);
          else fetchByWallet(wallet);
          return;
        }

        // If new order for this wallet or for this student, refresh
        const w = (detail.buyer && (detail.buyer.wallet || detail.buyer.from || detail.buyer.contact)) || (detail.meta && (detail.meta.wallet || detail.meta.from)) || null;
        const orderId = detail && detail.id;
        if (w && wallet && String(w).toLowerCase() === String(wallet).toLowerCase()) {
          if (Array.isArray(orderHistory) && orderHistory.length > 0) fetchByOrderHistory(orderHistory);
          else fetchByWallet(wallet);
          return;
        }
        if (student && orderId && Array.isArray(orderHistory) && orderHistory.includes(orderId)) fetchByOrderHistory(orderHistory);
      } catch (e) {}
    };
    window.addEventListener('store:orderCreated', onNew);
    window.addEventListener('orders-updated', onNew);
    // react to student updates so orderHistory / payments refresh
    const onStudentUpdated = (e) => {
      try {
        const detail = e && e.detail ? e.detail : null;
        if (!detail) return;
        if (student && detail.id === student.id) {
          // refresh orders/payments
          if (Array.isArray(detail.orderHistory) && detail.orderHistory.length > 0) fetchByOrderHistory(detail.orderHistory);
          else if (Array.isArray(orderHistory) && orderHistory.length > 0) fetchByOrderHistory(orderHistory);
          else fetchByWallet(wallet);
          fetchPaymentsForStudent(detail);
        }
      } catch (err) {}
    };
    window.addEventListener('student-updated', onStudentUpdated);
    window.addEventListener('students-synced', onStudentUpdated);
    return () => {
      window.removeEventListener('store:orderCreated', onNew);
      window.removeEventListener('orders-updated', onNew);
      window.removeEventListener('student-updated', onStudentUpdated);
      window.removeEventListener('students-synced', onStudentUpdated);
    };
  }, [wallet, fetchByWallet, orderHistory, student, fetchByOrderHistory, fetchPaymentsForStudent]);


  const grouped = orders.reduce((acc, o) => {
    try {
      const items = Array.isArray(o.items) ? o.items : (o.items ? [o.items] : []);
      const cat = items.length ? detectCategory(items[0]) : 'Other';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(o);
    } catch (e) { if (!acc.Other) acc.Other = []; acc.Other.push(o); }
    return acc;
  }, {});

  return (
    <div className="store-wrap" style={{ padding: 12 }}>
      <h3>Purchase History</h3>
      {payments && payments.length > 0 && (
        <div style={{ marginBottom: 8, fontSize: 13, color: 'var(--portal-muted)' }}>
          Payments: {payments.length} • Last: {payments[payments.length-1]?.timestamp ? new Date(payments[payments.length-1].timestamp).toLocaleDateString() : 'n/a'}
        </div>
      )}
      {!wallet && <div className="empty">Connect your wallet to view purchases.</div>}
      {loading && <div>Loading…</div>}
      {error && <div style={{ color: 'var(--danger)' }}>Error: {error}</div>}

      {!loading && !error && orders.length === 0 && <div className="empty">No purchases found for this wallet.</div>}

      {Object.keys(grouped).map(cat => (
        <div key={cat} style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 800, marginBottom: 8 }}>{cat} ({grouped[cat].length})</div>
          <div className="store-grid">
            {grouped[cat].map(o => {
              const items = Array.isArray(o.items) ? o.items : (o.items ? [o.items] : []);
              const nftStatus = (o.meta && o.meta.nftMinted) ? 'Minted' : (o.meta && o.meta.nftRetryQueued) ? 'Retrying' : ((o.nft && o.nft.txHash) || (o.meta && o.meta.nftTxHash)) ? 'Minted' : 'Pending';
              const tx = (o.nft && o.nft.txHash) || (o.meta && (o.meta.nftTxHash || o.meta.nftTxhash)) || null;
              return (
                <div key={o.id} className={`store-card`}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <div style={{ fontWeight: 800 }}>{short(o.id)}</div>
                      <div className="store-meta" style={{ fontSize: 12 }}>{new Date(o.createdAt || o.date || o.created || Date.now()).toLocaleString()}</div>
                      <div style={{ marginLeft: 'auto', fontWeight: 700 }}>{(o.total || o.amount || o.totalPrice || 0).toLocaleString(undefined, { style: 'currency', currency: 'USD' })}</div>
                    </div>
                    <div style={{ marginTop: 8 }}>
                      {items.map((it, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                          <div>{it.name || it.id}</div>
                          <div className="store-meta">{it.qty || it.quantity || 1} × ${(it.price || it.unitPrice || 0).toFixed(2)}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
                      <div style={{ fontSize: 13 }}>{nftStatus}{tx ? ` • ${short(tx)}` : ''}</div>
                      {nftStatus === 'Pending' && (
                        <div style={{ marginLeft: 8, padding: '6px 10px', borderRadius: 6, background: 'rgba(255,235,59,0.08)', color: '#ffd54f', fontWeight: 700, fontSize: 12 }}>
                          Purchase Confirmed — NFT Pending
                        </div>
                      )}
                      {tx && <a className="btn-sm" href={`https://etherscan.io/tx/${tx}`} target="_blank" rel="noreferrer">View NFT</a>}
                      {!tx && (o.meta && (o.meta.nftMinted || o.meta.nftTxHash)) && <button className="btn-sm" onClick={() => onViewCollectable(o)}>View NFT</button>}
                      <button className="btn-sm" onClick={() => alert(JSON.stringify(o, null, 2))}>View Metadata</button>
                      <button className="btn-sm" onClick={() => { try { window.dispatchEvent(new CustomEvent('navigateToCollectable', { detail: { order: o } })); } catch (e) {} }}>Open Collectable</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
